import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useRef } from 'react';

import { RefreshCcwIcon } from '@air/design-icons';
import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionButton } from '~/components/FileStatusTrackingPane/TaskPaneActionButton';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type GetCompletedMetadata, type ZippingTask } from '~/store/tasks/types';
import { DownloadFile, isS3DownloadUrlActive } from '~/utils/DownloadFile';

export type ZippingTaskCompletedPanelItemProps = {
  metadata: GetCompletedMetadata<ZippingTask>;
  onClear: () => void;
  onHandleExpiredLink: () => void;
};

export const ZippingTaskCompletedPanelItem = memo(
  ({
    metadata: { url, clipIds = [], boardIds = [], collectionIds = [] },
    onClear,
    onHandleExpiredLink,
  }: ZippingTaskCompletedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const downloadedRef = useRef<boolean>();
    /**
     * By being in ref, it prevents the `download` function from changing
     * and thus not triggering the download `useEffect` below.
     */
    const handleExpiredLinkRef = useRef(onHandleExpiredLink);
    handleExpiredLinkRef.current = onHandleExpiredLink;

    const download = useCallback((url: string) => {
      /**
       * This ref is to ensure the user doesn't start automically downloading the zip again.
       * After they start the download, we don't clear the downloaded item because it's possible
       * their download never started but we don't want to potentially automatically download again
       */
      if (isS3DownloadUrlActive(url) && !downloadedRef.current) {
        DownloadFile(url);
        downloadedRef.current = true;
      } else {
        handleExpiredLinkRef.current();
      }
    }, []);

    const retryDownload = useCallback(() => {
      if (!url) {
        handleExpiredLinkRef.current();
        return;
      }

      downloadedRef.current = false;
      download(url);
    }, [download, url]);

    useEffect(() => {
      if (url) {
        download(url);
      }
    }, [download, url]);

    const itemCount = clipIds.length + boardIds.length + collectionIds.length;

    return (
      <PaneItemTW
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="ZIPPING_PANEL_COMPLETED_TITLE">{`Zipped ${pluralize(
            'item',
            itemCount,
            true,
          )}`}</FileStatusTitle>
        }
        subtitle={
          ui2026 ? undefined : (
            <FileStatusSubtitle className="truncate" data-testid="ZIPPING_PANEL_COMPLETED_SUBTITLE">
              {
                <>
                  {`Download didn't start? `}
                  <button
                    className="font-semibold ads:text-accent text-12 text-altitude-9 ads:hover:text-accent-hover hover:text-altitude-11 hover:underline"
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={() => {
                      if (url) {
                        download(url);
                      }
                    }}
                    type="button"
                  >
                    Click here
                  </button>
                </>
              }
            </FileStatusSubtitle>
          )
        }
        buttons={
          <>
            {ui2026 ? (
              <TaskPaneActionButton
                data-testid="ZIPPING_PANEL_RETRY_BUTTON"
                icon={<RefreshCcwIcon size="sm" />}
                onClick={retryDownload}
              >
                Retry download
              </TaskPaneActionButton>
            ) : null}
            <TaskPaneDismissButton label="Clear" onClick={onClear} />
          </>
        }
      />
    );
  },
);

ZippingTaskCompletedPanelItem.displayName = 'ZippingCompletedPanelItem';
